import * as React from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogForm, DialogHeader,
  DialogTitle, DiscardGuard,
} from "@/components/ui/dialog";
import { Callout } from "@/components/ui/feedback";
import { Field } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/components/ui/toast";
import { AccessEditor } from "@/features/form-access";
import { ApiError } from "@/lib/api";
import {
  NEW_SECTION, SECTION_TITLE_MAX, activeFieldCount, archiveSectionBlocker, archiveSectionPayload,
  archivedSummary, keyFromTitle, newSectionPayload, recordNoun, sectionChanges, sectionKeys,
  sectionProblems, sectionValues, type ArchiveChoice, type SectionValues,
} from "@/lib/forms";
import { useCreateFormSection, useUpdateFormSection } from "@/lib/queries";
import type { AdminForm, AdminFormSection, FormEntity } from "@/lib/types";
import { cn } from "@/lib/utils";

/**
 * Add a folder (the API's section) to a contact's or a firm's custom fields, or edit one (ACL-256): its
 * name, and what each role may do with the fields in it that have no setting of their own.
 *
 * A folder is the heading a group of fields shares on the record. The name it is stored by is
 * made from its title and typed once, on create, as a stage's slug is: a field is placed in a
 * section by it, and the layout names the section by it, so it is not renamed afterwards; the
 * title is.
 */
export function FormSectionDialog({
  open, onOpenChange, section, form, entity, tenantId,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Absent for create. */
  section?: AdminFormSection;
  /** The design, for the stored names already taken. */
  form: AdminForm | undefined;
  entity: FormEntity;
  /** A superadmin's tenant; null for everyone else, whose own it is. */
  tenantId: number | null;
}) {
  const toast = useToast();
  const create = useCreateFormSection();
  const update = useUpdateFormSection();
  const [values, setValues] = React.useState<SectionValues>(NEW_SECTION);
  const [initial, setInitial] = React.useState<SectionValues>(NEW_SECTION);
  /** Whether the stored name still follows the title: until somebody types in it. */
  const [keyFollows, setKeyFollows] = React.useState(true);
  const [tried, setTried] = React.useState(false);
  const [formError, setFormError] = React.useState<string | null>(null);
  const taken = React.useMemo(() => sectionKeys(form), [form]);

  React.useEffect(() => {
    if (!open) return;
    const start = section ? sectionValues(section) : NEW_SECTION;
    setValues(start);
    setInitial(start);
    setKeyFollows(!section);
    setTried(false);
    setFormError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, section]);

  const set = <K extends keyof SectionValues>(name: K, value: SectionValues[K]) =>
    setValues((v) => {
      const next = { ...v, [name]: value };
      if (name === "title" && keyFollows && !section) next.key = keyFromTitle(String(value), taken);
      return next;
    });

  const creating = !section;
  const problems = sectionProblems(values, taken, creating);
  const changed = JSON.stringify(values) !== JSON.stringify(initial);
  const pending = create.isPending || update.isPending;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setTried(true);
    setFormError(null);
    if (Object.keys(problems).length > 0) return;
    try {
      if (section) {
        const changes = sectionChanges(values, section);
        if (Object.keys(changes).length > 0) {
          await update.mutateAsync({ entity, tenantId, key: section.key, ...changes });
        }
        toast.success("Folder saved", values.title.trim());
      } else {
        await create.mutateAsync({ entity, tenantId, ...newSectionPayload(values) });
        toast.success("Folder added", values.title.trim());
      }
      onOpenChange(false);
    } catch (err) {
      setFormError(err instanceof ApiError ? err.message : "Could not save the folder.");
    }
  };

  const shown = (name: keyof SectionValues) => (tried ? problems[name] : undefined);
  // The stored name is checked as it is typed: it is the one field that cannot be fixed later.
  const keyError = creating && (values.key || tried) ? problems.key : undefined;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent variant="drawer">
        <DiscardGuard changed={changed} />
        <DialogHeader>
          <DialogTitle>{section ? "Edit folder" : `New ${recordNoun(entity)} folder`}</DialogTitle>
          <DialogDescription>
            {section
              ? "Its name, and who can see and edit its fields. Its key is fixed."
              : `A group of fields on every ${recordNoun(entity)}, shown as one card on the record. Add fields once it exists.`}
          </DialogDescription>
        </DialogHeader>

        <DialogForm onSubmit={(e) => void submit(e)} noValidate>
          <DialogBody className="space-y-4">
            {formError && <Callout tone="danger">{formError}</Callout>}

            <div className="grid gap-4 sm:grid-cols-2">
              <Field
                label="Folder name"
                htmlFor="form-section-title"
                required
                error={shown("title")}
                hint={`At most ${SECTION_TITLE_MAX} characters.`}
              >
                <Input
                  autoComplete="off"
                  placeholder="Required Intake Questions"
                  invalid={Boolean(shown("title"))}
                  value={values.title}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => set("title", e.target.value)}
                />
              </Field>
              <Field
                label="Key"
                htmlFor="form-section-key"
                required={creating}
                error={keyError}
                hint={
                  creating
                    ? "What imports and integrations call the folder. It cannot change later."
                    : "Fixed."
                }
              >
                <Input
                  // Read-only rather than disabled, as on the other catalogs' forms.
                  readOnly={!creating}
                  className={cn("font-mono", !creating && "bg-surface-sunken text-text-muted")}
                  autoComplete="off"
                  placeholder="required_intake_questions"
                  invalid={Boolean(keyError)}
                  value={values.key}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    setKeyFollows(false);
                    set("key", e.target.value);
                  }}
                />
              </Field>
            </div>
            <AccessEditor
              kind="folder"
              idPrefix="form-section-access"
              value={values.access}
              onChange={(access) => set("access", access)}
            />
          </DialogBody>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" variant="default" loading={pending}>
              {section ? "Save changes" : "Add folder"}
            </Button>
          </DialogFooter>
        </DialogForm>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Archiving a folder. Nothing is deleted: an archived section and its fields leave the form and keep
 * their values, and each can be restored. One that still holds active fields cannot simply be
 * archived, so the dialog asks what becomes of them — move them to another section, or archive them
 * with it — and the server is told which. One with none is a plain confirm.
 */
export function ArchiveSectionDialog({
  section, sections, entity, tenantId, onOpenChange,
}: {
  section: AdminFormSection | undefined;
  /** Every section of the form: the ones the fields can move to are its other active ones. */
  sections: readonly AdminFormSection[];
  entity: FormEntity;
  tenantId: number | null;
  onOpenChange: (open: boolean) => void;
}) {
  const toast = useToast();
  const update = useUpdateFormSection();
  const others = React.useMemo(
    () => (section ? sections.filter((s) => !s.archived && s.key !== section.key) : []),
    [sections, section],
  );
  const [mode, setMode] = React.useState<ArchiveChoice["mode"]>("move");
  const [into, setInto] = React.useState("");
  const [problem, setProblem] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!section) return;
    // The section just before it, else the first that can take its fields; with none, they go with it.
    const active = sections.filter((s) => !s.archived);
    const before = active[active.findIndex((s) => s.key === section.key) - 1];
    setMode(others.length > 0 ? "move" : "archive");
    setInto(before?.key ?? others[0]?.key ?? "");
    setProblem(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [section?.key]);

  if (!section) return null;
  const live = activeFieldCount(section);
  const choice: ArchiveChoice = mode === "move" ? { mode, to: into } : { mode };
  const target = sections.find((s) => s.key === into);

  const confirm = () => {
    if (live > 0 && mode === "move" && !into) {
      setProblem("Pick the folder its fields move to.");
      return;
    }
    update.mutate(
      { entity, tenantId, key: section.key, ...archiveSectionPayload(section, choice) },
      {
        onSuccess: () => {
          onOpenChange(false);
          toast.success("Folder archived", archivedSummary(section, choice, target?.title ?? null));
        },
        onError: (e) =>
          setProblem(e instanceof ApiError ? e.message : "Could not archive the folder. Please try again."),
      },
    );
  };

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Archive {section.title}?</DialogTitle>
          <DialogDescription>
            {archiveSectionBlocker(section) ?? `${section.title} has no fields in use.`} An archived folder
            leaves the record and can be restored; nothing is deleted.
          </DialogDescription>
        </DialogHeader>
        <DialogBody className="space-y-4">
          {live > 0 && (
            <fieldset className="space-y-2">
              <legend className="sr-only">What becomes of its fields</legend>
              <Choice
                checked={mode === "move"}
                disabled={others.length === 0}
                onSelect={() => { setMode("move"); setProblem(null); }}
                title="Move its fields to another folder"
                description={
                  others.length > 0
                    ? "They keep their values and stay on the form."
                    : "There is no other folder to move them to."
                }
              />
              {mode === "move" && others.length > 0 && (
                <Field label="Move them to" htmlFor="archive-section-into" required>
                  <Select value={into} onValueChange={(v) => { setInto(v); setProblem(null); }}>
                    <SelectTrigger id="archive-section-into" aria-label="Move them to">
                      <SelectValue placeholder="Pick a folder" />
                    </SelectTrigger>
                    <SelectContent>
                      {others.map((s) => (
                        <SelectItem key={s.key} value={s.key}>
                          {s.title}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
              )}
              <Choice
                checked={mode === "archive"}
                onSelect={() => { setMode("archive"); setProblem(null); }}
                title="Archive them with it"
                description="They leave the record too. Nothing is deleted: restore the folder, then any field, to bring it back."
              />
            </fieldset>
          )}
          {problem && <Callout tone="danger">{problem}</Callout>}
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button variant="default" loading={update.isPending} onClick={confirm}>
            Archive folder
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** One of two ways out, as the other dialogs draw a choice: a bordered row that lights when picked. */
function Choice({
  checked, disabled, onSelect, title, description,
}: {
  checked: boolean;
  disabled?: boolean;
  onSelect: () => void;
  title: string;
  description: string;
}) {
  return (
    <label className="flex cursor-pointer items-start gap-2.5 rounded-md border border-border px-3 py-2.5 has-[:checked]:border-accent-8 has-[:checked]:bg-accent-2 has-[:disabled]:cursor-default has-[:disabled]:opacity-60">
      <input
        type="radio"
        name="archive-section-fields"
        className="mt-0.5 accent-accent-9"
        checked={checked}
        disabled={disabled}
        onChange={onSelect}
      />
      <span className="min-w-0">
        <span className="block text-xs font-medium text-text">{title}</span>
        <span className="block text-xs text-text-muted">{description}</span>
      </span>
    </label>
  );
}
